import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  TrendingUp, TrendingDown, Printer, FileDown, Calendar, ChevronLeft, ChevronRight,
  BarChart3, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, LineChart, Line,
} from 'recharts';
import { toast } from 'sonner';

const formatCurrency = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const currentMonthYYYYMM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// For a "YYYY-MM" string return [firstDayISO, lastDayISO] as pure strings
// (no Date→ISO round-trip — that introduces a timezone shift and pulls in
// the previous month when running in IST/any UTC+ timezone).
const monthBounds = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate(); // local .getDate() is TZ-safe
  const pad = (n) => String(n).padStart(2, '0');
  return [`${yyyymm}-01`, `${yyyymm}-${pad(daysInMonth)}`];
};

const formatMonthLabel = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const PLStatementDialog = ({ open, onOpenChange }) => {
  const [selectedMonth, setSelectedMonth] = useState(currentMonthYYYYMM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [start, end] = monthBounds(selectedMonth);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/pl-statement/pharmacy?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      setData(d);
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to load P&L statement'));
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const changeMonth = (delta) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1); // anchor day=1 is TZ-safe
    const pad = (n) => String(n).padStart(2, '0');
    setSelectedMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  };

  const handlePrint = () => {
    if (!data) return;
    const html = buildPrintHTML(data, start, end);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 1500); };
    }
  };

  const handleDownloadHTML = () => {
    if (!data) return;
    const html = buildPrintHTML(data, start, end);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PL_Pharmacy_${start}_to_${end}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success('Statement downloaded');
  };

  const totals = data?.totals || {};
  const monthly = data?.monthly || [];
  const counts = data?.counts || {};
  const isProfit = (totals.net_profit || 0) >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="pl-statement-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading tracking-tight flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-600" /> P&L Statement — Shri Kripa Pharmacy
          </DialogTitle>
          <DialogDescription>
            Profit = Sales − Purchases − Expenses · Source: pharmacy sales/purchases + Pharmacy-tagged expenses
          </DialogDescription>
        </DialogHeader>

        {/* Month Selector — matches Sale/Purchase style */}
        <Card className="p-4 border-slate-200 bg-slate-50/50">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Month-wise P&amp;L</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
              <button
                onClick={() => changeMonth(-1)}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="pl-prev-month-btn"
                title="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1.5 px-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <span
                  className="text-sm font-medium min-w-[130px] text-center"
                  data-testid="pl-current-month-label"
                >
                  {formatMonthLabel(selectedMonth)}
                </span>
              </div>
              <button
                onClick={() => changeMonth(1)}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="pl-next-month-btn"
                title="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Card>

        {loading && !data ? (
          <p className="text-center text-slate-500 py-12">Loading P&L data…</p>
        ) : !data ? (
          <p className="text-center text-slate-500 py-12">No data.</p>
        ) : (
          <>
            {/* Top summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SummaryCard
                label="Revenue (Sales)"
                value={formatCurrency(totals.total_sales)}
                tone="emerald"
                icon={TrendingUp}
                sub={`${counts.sales || 0} sale entries`}
              />
              <SummaryCard
                label="Total Outflow"
                value={formatCurrency((totals.total_purchases || 0) + (totals.total_expenses || 0))}
                tone="rose"
                icon={TrendingDown}
                sub={`${counts.purchases || 0} purchases · ${counts.expenses || 0} expenses`}
              />
              <SummaryCard
                label={isProfit ? 'Net Profit' : 'Net Loss'}
                value={formatCurrency(Math.abs(totals.net_profit || 0))}
                tone={isProfit ? 'blue' : 'amber'}
                icon={isProfit ? ArrowUpRight : ArrowDownRight}
                sub={`Margin: ${
                  totals.total_sales
                    ? ((totals.net_profit / totals.total_sales) * 100).toFixed(1)
                    : '0.0'
                }%`}
              />
            </div>

            {/* Detailed P&L */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide">
                Profit &amp; Loss Statement
              </div>
              <table className="w-full text-sm" data-testid="pl-detail-table">
                <tbody>
                  <PLRow label="Total Sales" value={totals.total_sales} positive />
                  <PLRow label="Less: Total Purchases" value={-(totals.total_purchases || 0)} negative />
                  <PLRow label="Gross Profit" value={totals.gross_profit} bold band="emerald" />
                  <PLRow label="Less: Total Expenses" value={-(totals.total_expenses || 0)} negative />
                  <PLRow
                    label={isProfit ? 'Net Profit' : 'Net Loss'}
                    value={totals.net_profit}
                    bold
                    band={isProfit ? 'blue' : 'amber'}
                  />
                </tbody>
              </table>
            </Card>

            {/* Charts */}
            {monthly.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4 border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Monthly Revenue vs Outflow</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="sales" fill="#10b981" name="Sales" />
                      <Bar dataKey="purchases" fill="#f43f5e" name="Purchases" />
                      <Bar dataKey="expenses" fill="#f59e0b" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-4 border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Profit Trend</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="gross_profit" stroke="#10b981" strokeWidth={2} name="Gross Profit" />
                      <Line type="monotone" dataKey="net_profit" stroke="#3b82f6" strokeWidth={2.5} name="Net Profit" />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}

            {/* Monthly breakdown table */}
            {monthly.length > 0 && (
              <Card className="border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700">
                  Monthly Breakdown
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Month</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-600">Sales</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-600">Purchases</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-600">Expenses</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-600">Gross Profit</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-600">Net Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.map((m) => (
                        <tr key={m.month} className="border-b border-slate-100">
                          <td className="px-4 py-2 font-medium">{m.month_label}</td>
                          <td className="px-4 py-2 text-right text-emerald-700">{formatCurrency(m.sales)}</td>
                          <td className="px-4 py-2 text-right text-rose-600">{formatCurrency(m.purchases)}</td>
                          <td className="px-4 py-2 text-right text-amber-600">{formatCurrency(m.expenses)}</td>
                          <td className={`px-4 py-2 text-right font-medium ${m.gross_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {formatCurrency(m.gross_profit)}
                          </td>
                          <td className={`px-4 py-2 text-right font-bold ${m.net_profit >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                            {formatCurrency(m.net_profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Action bar */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={handleDownloadHTML} data-testid="pl-download-btn">
                <FileDown className="h-4 w-4 mr-2" /> Download
              </Button>
              <Button
                onClick={handlePrint}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="pl-print-btn"
              >
                <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SummaryCard = ({ label, value, sub, icon: Icon, tone }) => {
  const tones = {
    emerald: { card: 'bg-emerald-50 border-emerald-200', icon: 'bg-emerald-600 text-white', text: 'text-emerald-700' },
    rose: { card: 'bg-rose-50 border-rose-200', icon: 'bg-rose-600 text-white', text: 'text-rose-700' },
    blue: { card: 'bg-blue-50 border-blue-200', icon: 'bg-blue-600 text-white', text: 'text-blue-700' },
    amber: { card: 'bg-amber-50 border-amber-200', icon: 'bg-amber-600 text-white', text: 'text-amber-700' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Card className={`p-5 border-2 ${t.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</p>
          <p className={`text-3xl font-heading tracking-tight mt-1 ${t.text}`}>{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1.5 truncate">{sub}</p>}
        </div>
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${t.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
};

const PLRow = ({ label, value, positive, negative, bold, band }) => {
  const bandClass = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
  }[band] || '';
  return (
    <tr className={`${bandClass} ${bold ? 'border-y-2 border-slate-300' : 'border-b border-slate-100'}`}>
      <td className={`px-4 py-2.5 ${bold ? 'font-bold text-base' : ''}`}>{label}</td>
      <td className={`px-4 py-2.5 text-right tabular-nums ${
        bold ? 'font-bold text-base' :
        positive ? 'text-emerald-700' :
        negative ? 'text-rose-600' :
        ''
      }`}>
        {formatCurrency(value)}
      </td>
    </tr>
  );
};

// ---- Print HTML builder ----
function buildPrintHTML(data, start, end) {
  const t = data.totals || {};
  const monthly = data.monthly || [];
  const isProfit = (t.net_profit || 0) >= 0;
  const monthRows = monthly.map((m) => `
    <tr>
      <td>${m.month_label}</td>
      <td class="num">${formatCurrency(m.sales)}</td>
      <td class="num">${formatCurrency(m.purchases)}</td>
      <td class="num">${formatCurrency(m.expenses)}</td>
      <td class="num pos">${formatCurrency(m.gross_profit)}</td>
      <td class="num ${m.net_profit >= 0 ? 'pos' : 'neg'} bold">${formatCurrency(m.net_profit)}</td>
    </tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>P&L Statement — Shri Kripa Pharmacy</title>
  <style>
    @page { size: A4; margin: 12mm; }
    body { font-family: Arial, sans-serif; color: #111; font-size: 11px; }
    h1 { margin: 0; font-size: 20px; }
    h2 { margin: 4px 0 16px; font-size: 12px; color: #555; font-weight: 500; }
    .meta { font-size: 11px; color: #666; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    th, td { border: 1px solid #d4d4d8; padding: 6px 8px; }
    th { background: #1e293b; color: #fff; font-size: 11px; text-align: left; }
    td.num { text-align: right; font-variant-numeric: tabular-nums; }
    td.pos { color: #047857; }
    td.neg { color: #be123c; }
    td.bold { font-weight: 700; }
    .pl-table tr.summary td { background: #f1f5f9; font-weight: 700; font-size: 13px; }
    .pl-table tr.final td { background: ${isProfit ? '#dbeafe' : '#fef3c7'}; font-weight: 700; font-size: 14px; color: ${isProfit ? '#1e40af' : '#92400e'}; }
    .footer { margin-top: 22px; font-size: 10px; color: #777; border-top: 1px solid #ddd; padding-top: 8px; }
  </style></head><body>
  <h1>Shri Kripa Pharmacy</h1>
  <h2>Profit &amp; Loss Statement</h2>
  <div class="meta">Period: <strong>${start}</strong> to <strong>${end}</strong> · Generated ${new Date().toLocaleString('en-IN')}</div>

  <table class="pl-table">
    <thead><tr><th>Particulars</th><th style="text-align:right">Amount (₹)</th></tr></thead>
    <tbody>
      <tr><td>Total Sales</td><td class="num pos">${formatCurrency(t.total_sales)}</td></tr>
      <tr><td>Less: Total Purchases</td><td class="num neg">−${formatCurrency(t.total_purchases)}</td></tr>
      <tr class="summary"><td>Gross Profit</td><td class="num">${formatCurrency(t.gross_profit)}</td></tr>
      <tr><td>Less: Total Expenses</td><td class="num neg">−${formatCurrency(t.total_expenses)}</td></tr>
      <tr class="final"><td>${isProfit ? 'Net Profit' : 'Net Loss'}</td><td class="num">${formatCurrency(Math.abs(t.net_profit || 0))}</td></tr>
    </tbody>
  </table>

  ${monthly.length > 0 ? `
  <h3 style="font-size:13px;margin:12px 0 6px">Monthly Breakdown</h3>
  <table>
    <thead><tr><th>Month</th><th style="text-align:right">Sales</th><th style="text-align:right">Purchases</th><th style="text-align:right">Expenses</th><th style="text-align:right">Gross Profit</th><th style="text-align:right">Net Profit</th></tr></thead>
    <tbody>${monthRows}</tbody>
  </table>` : ''}

  <div class="footer">Profit = Sales − Purchases − Expenses · Sales/Purchases from pharmacy ledger · Expenses are auto-synced from Pharmacy A/C Statement (category "Pharmacy")</div>
  </body></html>`;
}

export default PLStatementDialog;
